import { Link } from "react-router-dom";
import { ArrowUpRight, Check, ShieldCheck, Zap, Wallet, Clock, Rocket } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import Testimonials from "@/components/Testimonials";
import { KEYWORDS, FAQ } from "@/lib/seoContent";
import {
  organizationLd,
  professionalServiceLd,
  webPageLd,
  breadcrumbLd,
  faqPageLd,
  servicePackageLd,
  reviewLd,
  howToLd,
} from "@/lib/structuredData";

const CONTENT = {
  en: {
    metaTitle: "Cheap Website Development for Companies · NetoVibe",
    metaDesc:
      "Cheap website development for companies from €600. Custom-coded, mobile-first, SEO-ready. Freelance craft, agency scale. Transparent flat-rate pricing.",
    eyebrow: "Affordable website development for companies",
    h1a: "Cheap Website Development",
    h1b: "For Companies That Refuse to Look Cheap.",
    lead:
      "Custom-coded, mobile-first business websites from €600. Transparent flat-rate pricing, no monthly lock-ins, no surprise invoices. Delivered by one senior developer — not outsourced to a template farm.",
    ctaPrimary: "Get a fixed quote",
    ctaSecondary: "See package prices",
    reasonsH2: "Why companies choose NetoVibe for affordable website development",
    reasons: [
      {
        icon: Wallet,
        t: "Genuinely cheap. Never cheap-looking.",
        d: "Flat-rate packages from €600 (single-page launch pad) to €1,500 (full corporate build). No hourly rates that mysteriously double.",
      },
      {
        icon: Zap,
        t: "Fast: 0.9 s first paint on 4G",
        d: "Hand-coded, no bloated page builders. Core Web Vitals green on day one — the metrics Google actually rewards.",
      },
      {
        icon: ShieldCheck,
        t: "GDPR & Impressum-ready by default",
        d: "Every build ships with a compliant Impressum + Datenschutz page, cookie-light architecture, and EU-hosted infrastructure options.",
      },
      {
        icon: Clock,
        t: "Live in 7–21 days",
        d: "A single-page landing goes live in a week. A five-page professional site in 2–3 weeks. Firm delivery date, always.",
      },
      {
        icon: Rocket,
        t: "SEO baked in — not upsold later",
        d: "Semantic HTML, per-page meta, canonical + hreflang, sitemap, robots.txt and schema.org structured data. Included, not an add-on.",
      },
      {
        icon: Check,
        t: "One senior developer, direct",
        d: "You email the person coding your site. No account managers, no ping-pong between juniors.",
      },
    ],
    howH2: "How affordable website development works at NetoVibe",
    steps: [
      {
        n: "01",
        t: "Brief in 15 minutes",
        d: "Send us your project by email, phone, or WhatsApp. We reply within one business day with a transparent flat quote — no discovery-call theatre.",
      },
      {
        n: "02",
        t: "Design & build",
        d: "You get a live staging URL after 3–5 working days. Two rounds of revisions included on every package.",
      },
      {
        n: "03",
        t: "Launch & handover",
        d: "Site goes live at your domain. You get a short handover doc, admin access, and a support window (1–6 months depending on package).",
      },
    ],
    inclH2: "What every affordable website development package includes",
    inclusions: [
      "Fully custom, hand-coded front-end (no WordPress bloat)",
      "Mobile-first responsive design (phone, tablet, laptop, 4K)",
      "Semantic HTML5 + schema.org structured data",
      "Meta titles, descriptions and canonical tags per page",
      "GDPR-compliant Impressum + Datenschutz pages",
      "sitemap.xml + robots.txt auto-generated",
      "Basic analytics integration on request",
      "Contact form saved to a secure inbox",
      "Written handover so you can update content yourself",
    ],
    pricingH2: "Transparent pricing for company website development",
    pricingLead:
      "Three packages. No hidden fees. No monthly retainer disguised as maintenance.",
    faqH2: "Frequently asked questions",
    trustH3: "Real client. Real result.",
    finalCtaTitle: "Ready for cheap website development that doesn't look cheap?",
    finalCtaLead:
      "Tell us what you need — get a transparent quote back within one business day.",
  },

  de: {
    metaTitle: "Günstige Website-Entwicklung für Firmen · NetoVibe",
    metaDesc:
      "Günstige Website-Entwicklung für Firmen ab 600 €. Individuell programmiert, mobile-first, SEO-ready. Transparente Festpreise, keine monatliche Bindung.",
    eyebrow: "Günstige Website-Entwicklung für Firmen",
    h1a: "Günstige Website-Entwicklung",
    h1b: "Für Firmen, die nicht billig wirken wollen.",
    lead:
      "Individuell programmierte, mobile-first Firmenwebsites ab 600 €. Transparente Festpreise, keine monatlichen Bindungen, keine Überraschungsrechnungen. Von einem Senior-Entwickler geliefert — nicht ausgelagert an eine Template-Fabrik.",
    ctaPrimary: "Festpreis-Angebot anfordern",
    ctaSecondary: "Paketpreise ansehen",
    reasonsH2: "Warum Firmen NetoVibe für günstige Website-Entwicklung wählen",
    reasons: [
      {
        icon: Wallet,
        t: "Wirklich günstig. Nie billig aussehend.",
        d: "Festpreis-Pakete von 600 € (einseitige Landingpage) bis 1.500 € (komplette Corporate-Website). Keine Stundensätze, die sich geheimnisvoll verdoppeln.",
      },
      {
        icon: Zap,
        t: "Schnell: 0,9 s First Paint auf 4G",
        d: "Handprogrammiert, keine überladenen Baukästen. Core Web Vitals ab Tag eins grün — die Metriken, die Google tatsächlich belohnt.",
      },
      {
        icon: ShieldCheck,
        t: "DSGVO & Impressum standardmäßig",
        d: "Jede Website bekommt eine konforme Impressum- und Datenschutz-Seite, cookie-arme Architektur und EU-Hosting-Optionen.",
      },
      {
        icon: Clock,
        t: "Live in 7–21 Tagen",
        d: "Eine einseitige Landingpage in einer Woche, eine fünfseitige Website in 2–3 Wochen. Immer mit festem Launch-Datum.",
      },
      {
        icon: Rocket,
        t: "SEO inklusive — nicht nachverkauft",
        d: "Semantisches HTML, Meta pro Seite, Canonical + hreflang, Sitemap, robots.txt und schema.org strukturierte Daten. Enthalten, kein Add-on.",
      },
      {
        icon: Check,
        t: "Ein Senior-Entwickler, direkt",
        d: "Sie schreiben direkt der Person, die Ihre Website programmiert. Keine Account-Manager, kein Junior-Ping-Pong.",
      },
    ],
    howH2: "So läuft günstige Website-Entwicklung bei NetoVibe ab",
    steps: [
      {
        n: "01",
        t: "Briefing in 15 Minuten",
        d: "Schicken Sie uns Ihr Projekt per E-Mail, Telefon oder WhatsApp. Innerhalb eines Werktags erhalten Sie ein transparentes Festpreisangebot — ohne Discovery-Call-Theater.",
      },
      {
        n: "02",
        t: "Design & Umsetzung",
        d: "Nach 3–5 Werktagen bekommen Sie einen Live-Staging-Link. In jedem Paket sind zwei Korrekturrunden enthalten.",
      },
      {
        n: "03",
        t: "Launch & Übergabe",
        d: "Die Website geht auf Ihrer Domain online. Sie erhalten ein kurzes Übergabe-Dokument, Admin-Zugang und ein Support-Fenster (1–6 Monate je nach Paket).",
      },
    ],
    inclH2: "Was jedes Paket für günstige Website-Entwicklung enthält",
    inclusions: [
      "Vollständig individuelles, handprogrammiertes Frontend (kein WordPress-Ballast)",
      "Mobile-first Responsive Design (Handy, Tablet, Laptop, 4K)",
      "Semantisches HTML5 + schema.org strukturierte Daten",
      "Meta-Titel, Beschreibungen und Canonical Tags pro Seite",
      "DSGVO-konformes Impressum + Datenschutz",
      "sitemap.xml + robots.txt automatisch generiert",
      "Analytics-Grundintegration auf Wunsch",
      "Kontaktformular direkt ins sichere Postfach",
      "Schriftliche Übergabe — Inhalte selbst pflegen",
    ],
    pricingH2: "Transparente Preise für Firmen-Website-Entwicklung",
    pricingLead:
      "Drei Pakete. Keine versteckten Kosten. Kein monatlicher Retainer als Wartung getarnt.",
    faqH2: "Häufig gestellte Fragen",
    trustH3: "Echter Kunde. Echtes Ergebnis.",
    finalCtaTitle:
      "Bereit für günstige Website-Entwicklung, die nicht billig aussieht?",
    finalCtaLead:
      "Erzählen Sie uns von Ihrem Projekt — innerhalb eines Werktags erhalten Sie ein transparentes Angebot.",
  },
};

const CheapWebDevelopment = () => {
  const { t, lang, formatPrice } = useApp();
  const c = CONTENT[lang];

  useSEO({
    title: c.metaTitle,
    description: c.metaDesc,
    path: "/affordable-website-development",
    lang,
    keywords: [
      ...KEYWORDS[lang].pricing,
      "website development",
      "website development cheap for companies",
      "cheap website development",
      "affordable website development",
      "günstige Website-Entwicklung",
      "günstige Webentwicklung für Firmen",
      "Website Entwicklung Firmen",
      "günstige Firmenwebsite",
      "billige Website erstellen",
      "custom website development company",
      "flat-rate website development",
      "fixed-price website development",
    ],
    jsonLd: [
      organizationLd(),
      professionalServiceLd(),
      webPageLd({
        path: "/affordable-website-development",
        name: c.metaTitle,
        description: c.metaDesc,
        lang,
      }),
      breadcrumbLd([
        { name: t.nav.home, path: "/" },
        { name: c.eyebrow, path: "/affordable-website-development" },
      ]),
      ...t.pricing.packages.map((p) =>
        servicePackageLd({
          name: p.name,
          description: p.tagline,
          price: p.priceEUR,
          currency: "EUR",
        }),
      ),
      reviewLd(),
      howToLd({
        name:
          lang === "de"
            ? "So läuft günstige Website-Entwicklung bei NetoVibe ab"
            : "How affordable website development works at NetoVibe",
        lang,
        steps: c.steps.map((s) => ({ name: s.t, text: s.d })),
      }),
      faqPageLd(FAQ[lang]),
    ],
  });

  return (
    <main data-testid="cheap-dev-page" className="pt-16 lg:pt-20">
      {/* HERO */}
      <section className="border-b border-[#0A0A0A] bg-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-16 lg:pb-24">
          <div className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">
            <span className="inline-block h-[1px] w-8 bg-[#3A00FF]" />
            {c.eyebrow}
          </div>
          <h1
            data-testid="cheap-dev-title"
            className="mt-8 text-4xl sm:text-5xl md:text-6xl lg:text-[7rem] leading-[0.9] tracking-tighter font-black text-[#0A0A0A] font-[Cabinet_Grotesk]"
          >
            {c.h1a}
            <br />
            <span className="italic text-[#3A00FF]">{c.h1b}</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base sm:text-lg text-[#525252] leading-relaxed">
            {c.lead}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="cheap-dev-cta-primary"
                className="h-12 px-6 rounded-none bg-[#0A0A0A] text-white hover:bg-[#3A00FF] text-xs tracking-widest uppercase font-bold shadow-[4px_4px_0px_#3A00FF] hover:shadow-[6px_6px_0px_#3A00FF] transition-all"
              >
                {c.ctaPrimary}
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button
                variant="ghost"
                className="h-12 px-6 rounded-none border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-xs tracking-widest uppercase font-bold"
              >
                {c.ctaSecondary}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* REASONS (H2) */}
      <section className="border-b border-[#0A0A0A] bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
          <h2 className="max-w-3xl text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight font-black text-[#0A0A0A] font-[Cabinet_Grotesk]">
            {c.reasonsH2}
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#0A0A0A]/20">
            {c.reasons.map((r, i) => {
              const Icon = r.icon;
              return (
                <article
                  key={i}
                  data-testid={`reason-${i}`}
                  className="p-8 border-r border-b border-[#0A0A0A]/20 bg-white"
                >
                  <Icon className="h-6 w-6 text-[#3A00FF]" aria-hidden />
                  <h3 className="mt-4 text-lg lg:text-xl tracking-tight font-bold font-[Cabinet_Grotesk]">
                    {r.t}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#525252]">
                    {r.d}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS (H2) */}
      <section className="border-b border-[#0A0A0A] bg-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
          <h2 className="max-w-3xl text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight font-black text-[#0A0A0A] font-[Cabinet_Grotesk]">
            {c.howH2}
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {c.steps.map((s) => (
              <article
                key={s.n}
                className="border border-[#0A0A0A] p-8 bg-white shadow-[4px_4px_0px_#0A0A0A]"
              >
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#3A00FF]">
                  Step {s.n}
                </div>
                <h3 className="mt-3 text-xl lg:text-2xl tracking-tight font-black font-[Cabinet_Grotesk]">
                  {s.t}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-[#525252]">
                  {s.d}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* INCLUSIONS (H2) */}
      <section className="border-b border-[#0A0A0A] bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight font-black font-[Cabinet_Grotesk]">
              {c.inclH2}
            </h2>
          </div>
          <ul className="lg:col-span-7 space-y-3">
            {c.inclusions.map((it, i) => (
              <li
                key={i}
                data-testid={`inclusion-${i}`}
                className="flex items-start gap-3 text-base border-b border-white/10 pb-3"
              >
                <Check className="h-4 w-4 mt-1 text-[#3A00FF] shrink-0" aria-hidden />
                <span className="text-white/90">{it}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PRICING SNAPSHOT (H2) — reuses the pricing packages */}
      <section className="border-b border-[#0A0A0A] bg-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
          <h2 className="max-w-3xl text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight font-black text-[#0A0A0A] font-[Cabinet_Grotesk]">
            {c.pricingH2}
          </h2>
          <p className="mt-4 max-w-2xl text-base text-[#525252]">{c.pricingLead}</p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 border-t border-l border-[#0A0A0A]">
            {t.pricing.packages.map((p) => (
              <article
                key={p.id}
                className="border-r border-b border-[#0A0A0A] p-8 bg-white flex flex-col"
              >
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#3A00FF]">
                  {p.id}
                </div>
                <h3 className="mt-3 text-xl lg:text-2xl tracking-tight font-black font-[Cabinet_Grotesk]">
                  {p.name}
                </h3>
                <div className="mt-6 text-4xl font-black tracking-tighter font-[Cabinet_Grotesk]">
                  {formatPrice(p)}
                </div>
                <p className="mt-3 text-sm text-[#525252] flex-1">{p.tagline}</p>
                <Link
                  to="/contact"
                  className="mt-6 inline-flex items-center gap-2 text-xs tracking-widest uppercase font-bold text-[#0A0A0A] hover:text-[#3A00FF]"
                >
                  {t.pricing.cta} <ArrowUpRight className="h-4 w-4" />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <Testimonials eyebrow={c.trustH3} title={c.trustH3} />

      {/* FAQ (H2) — mirrors the FAQPage JSON-LD */}
      <section className="border-b border-[#0A0A0A] bg-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
          <h2 className="max-w-3xl text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight font-black text-[#0A0A0A] font-[Cabinet_Grotesk]">
            {c.faqH2}
          </h2>
          <div className="mt-12 divide-y divide-[#0A0A0A]/15 border-y border-[#0A0A0A]/15">
            {FAQ[lang].map((f, i) => (
              <details
                key={i}
                data-testid={`faq-${i}`}
                className="group py-6"
              >
                <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                  <h3 className="text-lg lg:text-xl tracking-tight font-bold font-[Cabinet_Grotesk] text-[#0A0A0A]">
                    {f.q}
                  </h3>
                  <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center border border-[#0A0A0A] text-[#0A0A0A] group-open:bg-[#0A0A0A] group-open:text-white transition-colors">
                    <ArrowUpRight className="h-4 w-4 rotate-45 group-open:rotate-90 transition-transform" />
                  </span>
                </summary>
                <p className="mt-4 text-sm lg:text-base leading-relaxed text-[#525252]">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter font-black text-[#0A0A0A] font-[Cabinet_Grotesk]">
              {c.finalCtaTitle}
            </h2>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-[#525252]">
              {c.finalCtaLead}
            </p>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Link to="/contact">
              <Button
                data-testid="cheap-dev-final-cta"
                className="h-14 px-8 rounded-none bg-[#3A00FF] text-white hover:bg-[#0A0A0A] text-xs tracking-widest uppercase font-bold shadow-[4px_4px_0px_#0A0A0A] hover:shadow-[6px_6px_0px_#0A0A0A] transition-all"
              >
                {c.ctaPrimary}
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CheapWebDevelopment;
